// src/pages/ManageWidgets/components/Widget/components/RichText/RichText.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import Quill from 'quill';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const FONT_SIZE_WHITELIST = [
  '16px', '10px', '11px', '12px', '13px', '14px', '15px', '18px', '20px', '22px',
  '24px', '28px', '32px', '36px', '40px', '48px'
];

const DEFAULT_FONT_SIZE = '16px';

const SizeStyle = Quill.import('attributors/style/size');
if (!SizeStyle.whitelist || SizeStyle.whitelist.join(',') !== FONT_SIZE_WHITELIST.join(',')) {
  SizeStyle.whitelist = FONT_SIZE_WHITELIST;
}
Quill.register(SizeStyle, true);

const TOOLBAR_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3, 4, 5, 6] }, { size: FONT_SIZE_WHITELIST }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const QUILL_FORMATS = [
  'header',
  'size',
  'bold', 'italic', 'underline',
  'color', 'background',
  'list', 'bullet',
  'link'
];

interface Props {
  content: string;
  onChange: (html: string) => void;
  debounceMs?: number;
}

export function RichText({ content, onChange, debounceMs = 500 }: Props) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const quillRef = useRef<ReactQuill | null>(null);

  const modules = useMemo(() => TOOLBAR_MODULES, []);

  const formats = useMemo(
    () => QUILL_FORMATS,
    []
  );

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      return;
    }
    editor.root.style.fontSize = DEFAULT_FONT_SIZE;
  }, []);

  const handleChange = useCallback((html: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange(html);
    }, debounceMs);
  }, [onChange, debounceMs]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return (
    <Box sx={{
      '& .ql-container': { minHeight: 200 },
      '& .ql-snow .ql-picker.ql-size .ql-picker-item::before': { content: 'attr(data-value) !important' },
      '& .ql-snow .ql-picker.ql-size .ql-picker-label[data-value]::before': { content: 'attr(data-value) !important' }
    }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={content || ''}
        onChange={handleChange}
        modules={modules}
        formats={formats}
      />
    </Box>
  );
}